import { exportNode, findNode } from "@/lib/tree/tree";
import { configStore, documentStore, uiStore } from "@/store/store";
import { TreeNode } from "@/types/tree";
import { fold, isRight } from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/function";
import {
  findPlaceToAddNodes,
  getDefaultTopLevelElementSlug,
  getNestingRuleFromNode
} from "@/lib/tree/nesting";
import { breakdanceUndo } from "@/lib/undo/undo";
import lodash from "lodash";
import defensiveCopy from "@/util/defensive-copy";
import fpJsonParseTypesafe from "@/util/fp-json-parse-typesafe";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import { getBuilderElementNameBySlug } from "@/lib/undo/snapshot-labels/document-store-property-change.utils";
import { breakdanceStoreActions } from "@/store/actions/store-actions";
import { v4 as uuidV4 } from "uuid";
import { EasyEvent } from "@/util/easy-event";
import {
  documentHasSelection,
  fallbackWriteToClipboard,
  getClipboardStatus
} from "@/lib/copy-paste/utils";
import { readFromLocalStorage, writeToLocalStorage } from "@/util/localstorage";
import { ClipboardData, ClipboardDataV } from "@/types/copy-paste";
import { getClipboardDataForElementOrElements } from "@/lib/copy-paste/element";
import { pasteElements } from "../design-library/design-library";

const clipboardEvents = new EasyEvent();

export function registerClipboardShortcuts() {
  return {
    register() {
      clipboardEvents.on("copy", onNativeEventReceived);
      clipboardEvents.on("cut", onNativeEventReceived);
      clipboardEvents.on("paste", onNativeEventReceived);
    },
    unbind() {
      clipboardEvents.removeAll();
    }
  };
}

function onNativeEventReceived(event: ClipboardEvent) {
  const iframeDoc = window.Breakdance.canvas.document;
  const hasSelection =
    documentHasSelection(iframeDoc) || documentHasSelection(document);

  if (hasSelection) return;

  event.preventDefault();

  if (event.type === "copy" && uiStore.activeElementId) {
    copyElement(uiStore.activeElementId);
  } else if (event.type === "cut" && uiStore.activeElementId) {
    cutElement(uiStore.activeElementId);
  } else if (event.type === "paste") {
    getElementToPasteAndPasteElement(uiStore.activeElementId, event);
  }
}

async function determinePastingMethod(event?: ClipboardEvent) {
  const { secure, permission } = await getClipboardStatus();

  // @ts-ignore
  if (event && !window.safari) return "event"; // Safari behaves strangely on non-ssl websites.
  if (!secure) return "localstorage";
  if (permission === null || permission.state === "denied") {
    return "localstorage";
  }

  return "clipboard";
}

export async function writeToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    console.debug("Writing to clipboard");
  } catch (err) {
    fallbackWriteToClipboard(text);
    console.debug("Fallback: Writing to clipboard");
  }

  writeToLocalStorage("breakdance_clipboard", text);
}

export async function readFromClipboard(event?: ClipboardEvent) {
  const eventText = event?.clipboardData?.getData("text");
  const method = await determinePastingMethod(event);
  console.debug(`Reading clipboard from ${method}`);

  // Accessing the clipboard from a native event requires no permission
  if (method === "event") {
    return eventText;
  }

  if (method === "localstorage") {
    return readFromLocalStorage("breakdance_clipboard");
  }

  return await navigator.clipboard.readText();
}

export async function getDataFromClipboard(
  event?: ClipboardEvent
): Promise<ClipboardData | null> {
  const clipboardJsonString = await readFromClipboard(event);

  if (!clipboardJsonString) return null;

  const maybeClipboardData = fpJsonParseTypesafe(
    clipboardJsonString,
    ClipboardDataV
  );

  return pipe(
    maybeClipboardData,
    fold(
      () => null,
      clipboardData => {
        return clipboardData ?? null;
      }
    )
  );
}

export function copyElements(elementIds: TreeNode["id"][]) {
  const exportedElements: TreeNode[] = [];

  elementIds.forEach(elementId => {
    const elementToCopy = findNode(documentStore.document.tree, elementId);
    if (elementToCopy) {
      exportedElements.push(exportNode(elementToCopy));
    }
  });

  const exportedElementsWithMetadata =
    getClipboardDataForElementOrElements(exportedElements);

  pipe(
    exportedElementsWithMetadata,
    fpJsonStringify,
    fold(
      e =>
        console.error(
          "Failed to copy element",
          ...e.map(err => err.toString())
        ),
      exportedElementsAsJsonString => {
        writeToClipboard(exportedElementsAsJsonString);
      }
    )
  );
}

export function copyElement(elementId: TreeNode["id"]) {
  const elementToCopy = findNode(documentStore.document.tree, elementId);
  if (!elementToCopy) return;

  const exportedElement = exportNode(elementToCopy);

  const exportedElementWithMetadata =
    getClipboardDataForElementOrElements(exportedElement);

  pipe(
    exportedElementWithMetadata,
    fpJsonStringify,
    fold(
      e =>
        console.error(
          "Failed to copy element",
          ...e.map(err => err.toString())
        ),
      exportedElementAsJsonString => {
        writeToClipboard(exportedElementAsJsonString);
      }
    )
  );
}

export function cutElement(elementId: TreeNode["id"]) {
  copyElement(elementId);
  documentStore.deleteElement({ nodeId: elementId });
}

export async function getElementToPasteAndPasteElement(
  elementIdToPasteNodesIntoOrNear: TreeNode["id"] | null,
  event?: ClipboardEvent
) {
  if (configStore.elementsLoadingState !== "success") return; // solves https://github.com/soflyy/breakdance/issues/5950

  const elementToPaste = (await getDataFromClipboard(event))?.element;

  if (!elementToPaste) return;

  if (Array.isArray(elementToPaste)) {
    pasteElements(elementToPaste, "pasting");
    return;
  } else {
    pasteElement(elementIdToPasteNodesIntoOrNear, elementToPaste);
  }
}

export function pasteElement(
  elementIdToPasteNodesIntoOrNear: TreeNode["id"] | null,
  elementToPaste: TreeNode
) {
  const placeToAdd = findPlaceToAddNodes(
    documentStore.document.tree,
    elementIdToPasteNodesIntoOrNear,
    getNestingRuleFromNode(elementToPaste)
  );

  /**
   * Snapshot bundling is required in order to merge the following
   * mutations into a single snapshot:
   *
   * a) documentStore.addElementMutation(), documentStore.pasteCopiedElementMutation()
   * b) mutations that are made by breakdanceStoreActions.run.onCreatedElement()
   *
   * Some mutations made by breakdanceStoreActions.onCreatedElement are async,
   * meaning they won't be included into this specific transaction.
   *
   * The transactionKey is passed to breakdanceStoreActions.run.onCreatedElement(), so
   * any transactions executed by the appropriate actions will use it in order to emerge
   * changes made by those transactions into a single snapshot generated by this specific transaction.
   *
   * We don't know the node ID for the pasted element yet.
   * Instead, a random number is included into the transactionKey
   */
  const uniqueId = uuidV4();
  const transactionKey = `Paste:${uniqueId}`;

  pipe(
    placeToAdd,
    fold(
      () => {},
      legalPlaceToAdd => {
        if (legalPlaceToAdd === "TOP_LEVEL") {
          breakdanceUndo.transaction(
            () => {
              const pointerForCreatedNodeId: { nodeId?: TreeNode["id"] } = {
                nodeId: undefined
              };

              documentStore.addElementMutation({
                elementSlug: getDefaultTopLevelElementSlug(),
                parentNodeId: documentStore.document.tree.root.id,
                index: false,
                pointer: pointerForCreatedNodeId
              });

              if (pointerForCreatedNodeId.nodeId !== undefined) {
                documentStore.pasteCopiedElementMutation({
                  element: elementToPaste,
                  placeToAddId: pointerForCreatedNodeId.nodeId,
                  placeToAddIndex: 0
                });

                breakdanceStoreActions.run.onCreatedElement({
                  elementId: elementToPaste.id,
                  transactionKey
                });
              }
            },
            `Paste copied ${getBuilderElementNameBySlug(
              elementToPaste.data.type
            )} into a new section`,
            transactionKey
          );
        } else {
          breakdanceUndo.transaction(
            () => {
              const pointerForCreatedNodeId: { nodeId?: TreeNode["id"] } = {
                nodeId: undefined
              };

              documentStore.pasteCopiedElementMutation({
                element: elementToPaste,
                placeToAddId: legalPlaceToAdd.place.id,
                placeToAddIndex:
                  legalPlaceToAdd.index ||
                  legalPlaceToAdd.place.children.length,
                pointer: pointerForCreatedNodeId
              });

              if (pointerForCreatedNodeId.nodeId) {
                breakdanceStoreActions.run.onCreatedElement({
                  elementId: pointerForCreatedNodeId.nodeId,
                  transactionKey
                });
              }
            },
            `Paste copied ${getBuilderElementNameBySlug(
              elementToPaste.data.type
            )}`,
            transactionKey
          );
        }
      }
    )
  );
}

export async function pasteElementProperties(
  elementIdToPastePropertiesTo: TreeNode["id"],
  path: "design" | "content" | "settings" = "design"
) {
  const elementToCopyPropertiesFrom = (await getDataFromClipboard())?.element;

  if (Array.isArray(elementToCopyPropertiesFrom)) return;
  if (!elementToCopyPropertiesFrom) return;

  const elementToPastePropertiesTo = findNode(
    documentStore.document.tree,
    elementIdToPastePropertiesTo
  );

  if (!elementToPastePropertiesTo) return;

  const copiedProperties = lodash.get(
    elementToCopyPropertiesFrom.data.properties,
    path,
    null
  );
  /*
    Setting to undefined actually blows up VueSimpleDeepset
    when you then try to set a control value that has previously been cleared
    by this function. so we use null as a fallback
    */
  if (
    elementToCopyPropertiesFrom.data.type ===
    elementToPastePropertiesTo.data.type
  ) {
    documentStore.throttledPropertyChanged({
      elementId: elementToPastePropertiesTo.id,
      path: path,
      value: defensiveCopy(copiedProperties),
      meta: {
        snapshotLabel: `Paste properties of ${getBuilderElementNameBySlug(
          elementToCopyPropertiesFrom.data.type
        )} to ${getBuilderElementNameBySlug(
          elementToPastePropertiesTo.data.type
        )}`
      }
    });
  }
}

export async function canPasteElement(
  elementIdToPasteNodesIntoOrNear: TreeNode["id"]
) {
  if (configStore.elementsLoadingState !== "success") return false; // solves https://github.com/soflyy/breakdance/issues/5950

  const elementOrElementsToPaste = (await getDataFromClipboard())?.element;

  /*
  we return true here, because we assume it's an array of sections.
  in breakdance, we don't allow you to copy anything other than an array of sections since the only way to 
  copy multiple elements to the clipboard is from the structure panel dropdown menu
  except...
   - editing a global block, you could copy non-sections
   - editing a popup
  so, that is a bug, but it's a bug that exists elsewhere in breakdance too - for example, you can't really copy/paste popups as far as i know
  */
  if (Array.isArray(elementOrElementsToPaste)) return true;

  if (!elementOrElementsToPaste) return false;

  const placeToAdd = findPlaceToAddNodes(
    documentStore.document.tree,
    elementIdToPasteNodesIntoOrNear,
    getNestingRuleFromNode(elementOrElementsToPaste)
  );

  return isRight(placeToAdd);
}

export async function canPasteElementProperties(
  elementIdToPastePropertiesTo: TreeNode["id"]
) {
  const elementOrElements = (await getDataFromClipboard())?.element;

  if (Array.isArray(elementOrElements)) return false;

  if (!elementOrElements) return false;

  const elementToPasteTo = findNode(
    documentStore.document.tree,
    elementIdToPastePropertiesTo
  );

  return elementOrElements.data.type === elementToPasteTo?.data.type;
}
